import { getDaysUntilEvent } from '@/resources/helpers';
import { storeToRefs } from 'pinia';
import { useProductStore } from '@/stores/product';
import { useAppConfig } from '#imports';
import {
  fromUnixTime,
  getFormattedDate,
  utcToZonedTime,
  formatDate,
  addMinutes,
  de,
  EVENTS_SLUG,
  MOTIV_SLUG_MALEN_ID,
  PRODUCT_EVENT_TYPE,
  PRODUCT_ONLINE_EVENT_TYPE
} from '@/resources/helpers';

function formatTime(obj) {
  const { years, months, days, hours } = obj;
  return `${years}y-${months}m-${days}d-${hours}h`;
}

// need to adjust for physicla products
export async function formatViewItem(code: string, index?: number, productData?: any) {
  const config = useAppConfig();
  const { $router } = useNuxtApp();
  let productInfo;
  if (productData) {
    productInfo = productData;
  } else {
    const productStore = useProductStore();
    await productStore.fetchProduct(String(code));
    const { product } = storeToRefs(productStore);
    productInfo = product.value;
  }

  return {
    pagePath: $router.currentRoute.value.path,
    pageUrl: window.location.href,
    pageTitle: document?.title,
    pageType: $router.currentRoute.value?.name?.replaceAll('-', ' '),
    currency: 'Euro',
    price: productInfo?.price,
    themeId: productInfo?.theme?.id?.toString(),
    hostId: productInfo?.hostUuid || 'no host id',
    locationId: productInfo?.venueUuid || 'no venue id',
    locationCity: [productInfo?.venueName || ''],
    locationName: productInfo?.venueUuid || 'no location id',
    locationPostcode: [''],
    locationCountry: ['DE'],
    itemName: productInfo?.title || 'no item name',
    motivLevel: [productInfo?.theme?.level || 'no motiv level'],
    category: [JSON.stringify(productInfo?.theme?.categories)],
    index: index?.toString() || '0',
    eventId: productInfo?.id?.toString(),
    themeUuid: productInfo?.themeUuid ? productInfo?.themeUuid : 'no data',
    productInfoType: productInfo?.__typename,
    daysUntilEvent: productInfo?.eventTimestamp
      ? formatTime(getDaysUntilEvent(productInfo?.eventTimestamp))
      : 'no date',
    ticketsAvailable: productInfo?.quantity?.toString()
      ? productInfo?.quantity?.toString()
      : 'no ticket data available',
    code: productInfo?.code,
    date: productInfo?.eventTimestamp
      ? getFormattedDate(fromUnixTime(productInfo?.eventTimestamp), 'dd-MM-yyyy', config.timeZone)
      : 'no date',
    city: productInfo?.cityName,
    taxon: productInfo?.main_taxon_id
  };
}

export function formatGtmItemProperty(item) {
  return {
    item_city: item?.locationCity,
    item_date: item?.eventStart,
    item_name: item?.itemName,
    item_id: item?.itemId?.toString(),
    price: item?.price ? item?.price / 100 : null,
    item_brand: item?.hostId,
    item_category: item?.qty,
    item_category_2: item?.locationName,
    item_category_3: item?.category,
    item_category_4: item?.motivLevel,
    item_list_id: item?.taxon,
    item_list_name: item?.locationCity,
    item_variant: item?.eventStart
  };
}

export function formatGroupEventsAddToCartIem(product) {
  return {
    themeId: product?.theme?.theme?.id?.toString(),
    hostId: '',
    listId: [''],
    category: [product?.theme?.theme?.categories.uuid || 'no data'],
    eventStart: product?.eventDate,
    price: product?.theme?.price,
    locationCountry: ['Germany'],
    locationId: product.locationId?.toString(),
    locationCity: [product?.locationCity],
    locationName: 'custom',
    locationPostcode: [product.locationPostalCode],
    itemName: product?.theme?.theme?.name,
    pageType: 'GroupEvent',
    listType: { type: 'GroupEvent' },
    itemType: '',
    daysUntilEvent: JSON.stringify(getDaysUntilEvent(product.eventDate)),
    quantity: product.numberOfParticipants,
    itemId: '',
    itemSku: product.themeSKU,
    taxon: product.main_taxon_id
    // eventId?: string
  };
}

export function formatGlobalItem(product: any) {
  const config = useAppConfig();
  const categoryStrings = product?.theme?.categories.map((category) => category.uuid) || [];

  return {
    themeId: product?.id?.toString(),
    hostId: product?.hostUuid?.toString() || product?.sku,
    listId: [''],
    category: categoryStrings,
    eventStart: product?.eventTimestamp
      ? getFormattedDate(fromUnixTime(product?.eventTimestamp), 'yyyy-MM-dd', config.timeZone)
      : 'no start date',
    price: product?.price,
    locationCountry: ['DE'],
    locationId: product?.venueUuid?.toString(),
    locationCity: [product?.cityName || product?.product_type || 'online'],
    locationName: product?.locationName || 'no data ',
    locationPostcode: ['missing venue'],
    itemName: product?.theme?.name,
    listType: {},
    itemType: product?.__typename,
    daysUntilEvent: product?.eventTimestamp ? formatTime(getDaysUntilEvent(product?.eventTimestamp)) : 'no date',
    itemId: product?.code?.toString(),
    itemSku: product?.sku || product?.sku,
    itemVariant: product?.slug || product?.sku,
    taxon: product?.main_taxon_id || product?.sku,
    motivLevel: product?.theme?.level || 'no data',
    qty: product?.order_qty || product?.qty
  };
}

export function formatVoucherItem(product: any) {
  return {
    themeId: product.title,
    hostId: 'no data',
    listId: [''],
    category: ['Gifcard'],
    eventStart: 'no start date',
    price: product?.price / 100,
    locationCountry: ['DE'],
    locationId: 'voucher',
    locationCity: ['voucher'],
    locationName: 'voucher',
    locationPostcode: ['missing venue'],
    itemName: product?.name,
    listType: {},
    itemType: product?.__typename,
    daysUntilEvent: 'no data',
    itemId: product?.gift_card_theme_id?.toString(),
    itemSku: product?.sku,
    itemVariant: product?.slug
  };
}

export function getQuery(routerQuery, query: string) {
  const queryVal = routerQuery.currentRoute?.value?.query;

  if (!queryVal || !Object.keys(queryVal).length) return `no ${query ?? 'query'}`;

  if (!query) {
    return JSON.stringify(queryVal);
  }

  if (query.length) {
    return queryVal[query]?.toString();
  }
}

export function prepareProduct(product) {
  if (!product) return null;
  const config = useAppConfig();
  const timeZone = config.timeZone;

  let adjustedProduct = {
    ...product,
    stock: {
      qty: product?.quantity,
      is_in_stock: true
    }
  };

  if ([PRODUCT_EVENT_TYPE, PRODUCT_ONLINE_EVENT_TYPE].includes(adjustedProduct.typeId) && product.eventTimestamp) {
    const unixTime = fromUnixTime(product.eventTimestamp);
    const zonedTime = utcToZonedTime(unixTime, timeZone);
    adjustedProduct = {
      ...product,
      event_date: {
        string: formatDate(zonedTime, 'yyyy-MM-dd k:mm', { locale: de, timeZone }),
        day: formatDate(zonedTime, 'yyyy-MM-dd', { locale: de, timeZone }),
        date: formatDate(zonedTime, 'dd.MM.yyyy', { locale: de, timeZone }),
        start_time: formatDate(zonedTime, 'k:mm', { locale: de, timeZone }),
        end_time: formatDate(addMinutes(zonedTime, product.eventDuration), 'k:mm', { locale: de, timeZone })
      },
      stock: {
        qty: product.quantity,
        is_in_stock: product.quantity > 0
      }
    };
  }

  return adjustedProduct;
}

export function prepareProducts(products) {
  return products.map((product) => {
    return prepareProduct(product);
  });
}

export function setTrackingData(val: string) {
  switch (val) {
    case EVENTS_SLUG:
      return 'Event PDP';
    case MOTIV_SLUG_MALEN_ID:
      return 'Motif PDP';
    case 'products-slug':
      return 'Product PDP';
    case 'groupevents-configurator':
      return 'Group Konfigurator';
    case 'themes-route':
      return 'Motiv PLP';
    case 'events-route':
      return 'Event PLP';
    case 'gutscheinkarte-kaufen':
      return 'Voucher configurator';
    case 'themes-route-kids':
      return 'Kids PLP';
    case 'checkout':
      return 'Checkout';
    case 'rebooking':
      return 'Rebooking';
    case 'resend-booking':
      return 'Resend booking';
    case 'payment-processing':
      return 'Payment processing';
    case 'payment-error':
      return 'Payment Error';
    case 'variable-route':
      return 'Variable Route';
  }
}
